import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  structureBillingData, getStructureById, getPharmacyById
} from "@/data/platformData";
import { doctorsData, getServiceById } from "@/data/mockData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Receipt, ArrowLeft, Download, Euro, Building2, Stethoscope } from "lucide-react";

const STRUCTURE_ID = "str1";

const fmtMonth = (m) => {
  const [y, mm] = m.split("-");
  const labels = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  return `${labels[Number(mm) - 1]} ${y}`;
};

export const StructureBillingPage = () => {
  const navigate = useNavigate();
  const structure = getStructureById(STRUCTURE_ID);
  const allRows = structureBillingData.filter(r => r.structureId === STRUCTURE_ID);

  const months = [...new Set(allRows.map(r => r.month))].sort().reverse();
  const [selectedMonth, setSelectedMonth] = useState(months[0]);

  const monthRows = allRows.filter(r => r.month === selectedMonth);

  const totalRevenue = useMemo(() => monthRows.reduce((acc, r) => acc + r.total, 0), [monthRows]);
  const totalCount = useMemo(() => monthRows.reduce((acc, r) => acc + r.count, 0), [monthRows]);

  // Group by pharmacy
  const byPharmacy = useMemo(() => {
    const map = {};
    monthRows.forEach(r => {
      if (!map[r.pharmacyId]) map[r.pharmacyId] = { pharmacyId: r.pharmacyId, count: 0, total: 0, services: {} };
      map[r.pharmacyId].count += r.count;
      map[r.pharmacyId].total += r.total;
      map[r.pharmacyId].services[r.serviceId] = (map[r.pharmacyId].services[r.serviceId] || 0) + r.count;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [monthRows]);

  // Group by doctor
  const byDoctor = useMemo(() => {
    const map = {};
    monthRows.forEach(r => {
      if (!map[r.specialistId]) map[r.specialistId] = { specialistId: r.specialistId, count: 0, total: 0 };
      map[r.specialistId].count += r.count;
      map[r.specialistId].total += r.total;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [monthRows]);

  // Trend across all months for chart
  const trendData = useMemo(() => months.map(m => {
    const rows = allRows.filter(r => r.month === m);
    return {
      month: fmtMonth(m),
      fatturato: rows.reduce((a, b) => a + b.total, 0),
      esami: rows.reduce((a, b) => a + b.count, 0)
    };
  }).reverse(), [months, allRows]);

  const exportCSV = () => {
    toast.success("Export CSV avviato", { description: `Rendicontazione ${fmtMonth(selectedMonth)} pronta per il download.` });
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/structure")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-4 w-4 text-cyan-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Rendicontazione</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Fatturato refertazioni</h1>
            <p className="text-sm text-muted-foreground mt-1">{structure.name}</p>
          </div>
          <Button variant="premium" onClick={exportCSV} className="gap-2" data-testid="billing-export-btn">
            <Download className="h-4 w-4" /> Esporta CSV
          </Button>
        </div>
      </div>

      {/* Month selector */}
      <div className="flex flex-wrap gap-2" data-testid="billing-month-selector">
        {months.map(m => (
          <Button
            key={m}
            variant={selectedMonth === m ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedMonth(m)}
            data-testid={`billing-month-${m}`}
          >
            {fmtMonth(m)}
          </Button>
        ))}
      </div>

      {/* KPI */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Euro className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">€ {totalRevenue.toLocaleString("it-IT")}</p>
              <p className="text-xs mt-1 text-muted-foreground">Fatturato {fmtMonth(selectedMonth)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
              <Receipt className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">{totalCount}</p>
              <p className="text-xs mt-1 text-muted-foreground">Refertazioni</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">{byPharmacy.length}</p>
              <p className="text-xs mt-1 text-muted-foreground">Farmacie attive</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pharmacies" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pharmacies" data-testid="billing-tab-pharmacies">Per farmacia</TabsTrigger>
          <TabsTrigger value="doctors" data-testid="billing-tab-doctors">Per medico</TabsTrigger>
          <TabsTrigger value="trend" data-testid="billing-tab-trend">Trend</TabsTrigger>
        </TabsList>

        <TabsContent value="pharmacies" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-600" /> Rendicontazione per farmacia convenzionata
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {byPharmacy.map(row => {
                const ph = getPharmacyById(row.pharmacyId);
                return (
                  <div key={row.pharmacyId} className="p-3 rounded-lg border bg-card" data-testid={`billing-pharmacy-${row.pharmacyId}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm">{ph?.name}</p>
                        <p className="text-xs text-muted-foreground">{ph?.address}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-700">€ {row.total.toLocaleString("it-IT")}</p>
                        <p className="text-xs text-muted-foreground">{row.count} refertazioni</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(row.services).map(([sid, n]) => (
                        <Badge key={sid} variant="secondary" className="text-[10px]">
                          {getServiceById(sid)?.name || sid}: {n}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="doctors" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-cyan-600" /> Rendicontazione per medico
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {byDoctor.map(row => {
                const d = doctorsData.find(x => x.id === row.specialistId);
                return (
                  <div key={row.specialistId} className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid={`billing-doctor-${row.specialistId}`}>
                    <div>
                      <p className="font-semibold text-sm">{d?.name}</p>
                      <p className="text-xs text-muted-foreground">{d?.specialization}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-700">€ {row.total.toLocaleString("it-IT")}</p>
                      <p className="text-xs text-muted-foreground">{row.count} refertazioni</p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Trend fatturato e volume</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis yAxisId="left" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" fontSize={12} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="fatturato" fill="#10b981" name="Fatturato (€)" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="esami" fill="hsl(var(--primary))" name="Esami" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StructureBillingPage;
